import React, { useState, useEffect } from "react";
// Importe o tipo SystemType da Navbar
import Navbar, { SystemType } from "../Navbar";
import Categorias from "../ComponenteCategorias/Categorias";
import AnaliseAnuncios from "../ComponenteAnaliseAnuncios/AnaliseAnuncios";
import GerenciarAdmin from "../ComponenteAdministradores/GerenciarAdmin";
import Planos from "../ComponentePlanos/Planos";
import Financeiro from "../ComponenteFinanceiro/Financeiro";
import Configuracao from "../Configuracao";
import GerenciarNotificacao from "../ComponenteNotificar/GerenciarNotificacao";
import { Box, Button, Typography } from "@mui/material"; // Adicionado Button e Typography
import GerenciarUsuarios from "../ComponenteUsuarios/GerenciarUsuarios";
import { API_URLS } from "../config/apiUrls";
import GerenciarSorteio from "../ComponenteSorteio/GerenciarSorteio";
import GerenciarCompeticao from "../Escala11/Competicao/GerenciarCompeticao";
import GerenciarComissoes from "../ComponenteComissoes/GerenciarComissoes";

interface PainelProps {
  role: "MASTER" | "PARTNER" | "ANALYST";
  token: string;
}

const handleLogout = () => {
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("role");
  // Opcional: remover o estado do sistema também
  sessionStorage.removeItem("currentSystem");
  window.location.href = ""; // Redirecionar para a tela de login
};

interface Item {
  label: string;
  onClick: () => void;
}

interface NavItems {
  mainItems: Array<Item>;
  systemItem: Item | null;
}

const Painel: React.FC<PainelProps> = ({ role, token }) => {
  const [selecao, setSelecao] = useState<string>("analise"); // Estado da sub-seção
  const [userToken, setUserToken] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<
    "MASTER" | "PARTNER" | "ANALYST" | null
  >(null);

  // NOVO ESTADO: Sistema ativo
  const [currentSystem, setCurrentSystem] = useState<SystemType>(
    (sessionStorage.getItem("currentSystem") as SystemType) || "MERCADOTERRA",
  );

  useEffect(() => {
    const storedToken = sessionStorage.getItem("token");
    const storedRole = sessionStorage.getItem("role");
    const storedSystem = sessionStorage.getItem("currentSystem") as SystemType;

    if (storedToken && storedRole) {
      setUserToken(storedToken);
      setUserRole(storedRole as "MASTER" | "PARTNER" | "ANALYST");
      setCurrentSystem(storedSystem || "MERCADOTERRA");

      if (storedSystem === "ESCALA11") {
        setSelecao("gerenciarCompeticao"); // Ou o item de menu padrão do Escala 11
      } else {
        setSelecao("analise"); // Item de menu padrão
      }
    } else {
      window.location.href = "";
    }
  }, []);

  // NOVO useEffect para gerenciar TÍTULO e FAVICON
  // Este hook será executado toda vez que 'currentSystem' mudar.
  useEffect(() => {
    // É uma boa prática ter um ID na tag <link rel="icon"> no seu index.html
    const link = document.getElementById(
      "favicon-link",
    ) as HTMLLinkElement | null;

    if (currentSystem === "ESCALA11") {
      // 1. Define o novo TÍTULO da página
      document.title = "Escala 11 | Painel de Controle";

      // 2. Define o novo FAVICON
      if (link) {
        // Altere o caminho abaixo para o local correto do seu ícone do Escala 11
        link.href = "/escala11.ico";
      }
    } else {
      // 1. Define o novo TÍTULO da página
      document.title = "Mercado Terra | Painel Administrativo";

      // 2. Define o novo FAVICON
      if (link) {
        // Altere o caminho abaixo para o local correto do seu ícone do Anúncio 360
        link.href = "/mercadoterra.ico";
      }
    }
  }, [currentSystem]); // Dependência: só roda quando o sistema muda

  const fetchProfile = async (token: string) => {
    /* ... */
  };

  // Função para alternar o sistema
  const toggleSystem = () => {
    const newSystem =
      currentSystem === "MERCADOTERRA" ? "ESCALA11" : "MERCADOTERRA";
    setCurrentSystem(newSystem);
    sessionStorage.setItem("currentSystem", newSystem);

    // Define a seleção inicial após a troca
    if (newSystem === "ESCALA11") {
      setSelecao("gerenciarCompeticao");
    } else {
      setSelecao("analise");
    }
  };

  // Renderiza a seção de CONTEÚDO com base na seleção e no sistema
  const renderSection = () => {
    const tokenToUse = userToken || token;
    const roleToUse = userRole || role;

    // Se o sistema for ESCALA11, renderize o conteúdo específico
    if (currentSystem === "ESCALA11") {
      switch (selecao) {
        case "sorteio":
          return (
            <GerenciarSorteio
              token={tokenToUse}
              role={roleToUse}
              system={currentSystem}
            />
          );
        case "gerenciarCompeticao":
          return <GerenciarCompeticao token={tokenToUse} />;
        default:
          return <GerenciarCompeticao token={tokenToUse} />;
      }
    }

    // Conteúdo
    switch (selecao) {
      case "categorias":
        return <Categorias token={tokenToUse} />;
      case "analise":
        return <AnaliseAnuncios token={tokenToUse} role={roleToUse} />;
      case "planos":
        return <Planos token={tokenToUse} />;
      case "financeiro":
        return <Financeiro token={tokenToUse} role={roleToUse} />;
      case "usuarios":
        return <GerenciarUsuarios token={tokenToUse} role={roleToUse} />;
      case "admins":
        return <GerenciarAdmin token={tokenToUse} role={roleToUse} />;
      case "comissoes":
        return <GerenciarComissoes token={tokenToUse} role={roleToUse} />;
      case "notificacao":
        return <GerenciarNotificacao token={tokenToUse} role={roleToUse} />;
      case "configuracoes":
        return <Configuracao token={tokenToUse} />;
      case "sorteio":
        return (
          <GerenciarSorteio
            token={tokenToUse}
            role={roleToUse}
            system={currentSystem}
          />
        );
      default:
        return <AnaliseAnuncios token={tokenToUse} role={roleToUse} />;
    }
  };

  const getItems = (): NavItems => {
    // Tipo de retorno atualizado
    const roleToUse = userRole;

    const mainItems: Array<Item> = [];
    let systemItem: Item | null = null; // Item do sistema

    // ITENS
    if (currentSystem === "MERCADOTERRA") {
      mainItems.push({
        label: "Anúncios",
        onClick: () => setSelecao("analise"),
      });

      if (roleToUse === "MASTER") {
        mainItems.push(
          { label: "Categorias", onClick: () => setSelecao("categorias") },
          { label: "Planos", onClick: () => setSelecao("planos") },
        );
      }

      if (roleToUse === "PARTNER" || roleToUse === "MASTER") {
        mainItems.push(
          { label: "Financeiro", onClick: () => setSelecao("financeiro") },
          { label: "Sorteio", onClick: () => setSelecao("sorteio") },
          { label: "Usuários", onClick: () => setSelecao("usuarios") },
        );
      }

      if (roleToUse === "MASTER") {
        mainItems.push(
          { label: "Administradores", onClick: () => setSelecao("admins") },
          { label: "Notificações", onClick: () => setSelecao("notificacao") },
          {
            label: "Comissões",
            onClick: () => setSelecao("comissoes"),
          },
          {
            label: "Configurações",
            onClick: () => setSelecao("configuracoes"),
          },
        );

        // ITEM DE ALTERNÂNCIA DE SISTEMA (separado)
        systemItem = {
          label: `${currentSystem === "MERCADOTERRA" ? "Escala 11" : "Mercado Terra"}`,
          onClick: toggleSystem,
        };
      }
    }

    // ITENS DO ESCALA 11
    else if (currentSystem === "ESCALA11") {
      mainItems.push(
        { label: "Sorteio", onClick: () => setSelecao("sorteio") },
        {
          label: "Competição",
          onClick: () => setSelecao("gerenciarCompeticao"),
        },
      );

      // ITEM DE ALTERNÂNCIA DE SISTEMA (separado)
      systemItem = {
        label: `${currentSystem === "ESCALA11" ? "Mercado Terra" : "Escala 11"}`,
        onClick: toggleSystem,
      };
    }

    return { mainItems, systemItem }; // Retorna o novo objeto
  };

  // Se o role ainda não tiver sido carregado, mostre um loading
  if (!userRole && !role) {
    return <Box sx={{ p: 5 }}>Carregando painel...</Box>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar
        onLogout={handleLogout}
        role={userRole || role}
        itens={getItems()}
        system={currentSystem}
        currentSelectionKey={selecao}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
        }}
      >
        {renderSection()}
      </Box>
    </Box>
  );
};

export default Painel;
